import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
  Signal,
  viewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { editor } from 'monaco-editor/esm/vs/editor/editor.api';
import { CODE_FIELD_DEFAULT_OPTIONS, CODE_FIELD_SCHEMA_BASE_URL } from './code-field.constant';
import { CodeFieldEditorModel } from './code-field.model';
import { fromEvent, Subscription } from 'rxjs';
import { ZuiCodeFieldService } from './code-field.service';
import { configureMonacoYaml } from 'monaco-yaml';
import { registerNginx } from './nginx-support';

@Component({
  selector: 'zui-code-field',
  templateUrl: './code-field.component.html',
  styleUrls: [ './code-field.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeFieldComponent),
      multi: true
    }
  ]
})
export class CodeFieldComponent implements ControlValueAccessor, OnDestroy {

  // # Deps
  #zone = inject(NgZone);
  #codeFieldService = inject(ZuiCodeFieldService);
  #schemaBaseUrl = inject(CODE_FIELD_SCHEMA_BASE_URL) ?? '';

  // # Data
  value = input<string>();
  editorModel = input<CodeFieldEditorModel>();
  loading = input<boolean>(false);
  editorOptions = input<Partial<editor.IStandaloneEditorConstructionOptions>>({});
  // auto-grow the editor with its content (one line per newline), clamped between
  // minLines and maxLines - beyond maxLines the editor stops growing and scrolls
  autoGrow = input<boolean>(false);
  minLines = input<number>(8);
  maxLines = input<number>(30);
  editorContainerRef = viewChild<ElementRef<HTMLElement>>('editorContainerRef');
  editorInit = output<editor.IStandaloneCodeEditor>();
  valueChange = output<string>();

  #value = '';
  // true while we push a value INTO the editor (writeValue). Monaco fires
  // onDidChangeModelContent for programmatic setValue too, and echoing that back
  // through onChange races with async form updates (e.g. a value set moments after
  // the editor seeds empty), clobbering the store - so we suppress the echo.
  #isWritingValue = false;
  #editorInstance: editor.IStandaloneCodeEditor;
  #editorOptions: Signal<editor.IStandaloneEditorConstructionOptions | undefined> = computed(() => {
    return {
      ...CODE_FIELD_DEFAULT_OPTIONS,
      ...(this.editorOptions() || {})
    };
  });
  #windowResizeSubscription: Subscription;

  onChange = (_: any) => { return; };
  onTouched = (_: any) => { return; };

  constructor() {
    effect(() => {
      if (this.editorContainerRef()
        && (this.#editorOptions() || this.editorModel())
      ) {
        if (this.#codeFieldService.loadingState() === 'NOT_LOADED') {
          this.#codeFieldService.load({
            onMonacoLoad: () => {
              registerNginx((window as any).monaco);

              configureMonacoYaml((window as any).monaco, {
                enableSchemaRequest: true,
                hover: true,
                completion: true,
                validate: true,
                format: true,
                schemas: this.#buildSchemas()
              });

            }
          });
        }

        if (this.#codeFieldService.loadingState() === 'LOADED') {
          this.#initMonaco();
        }
      }
    }, { allowSignalWrites: true });

    effect(() => {
      if (this.editorContainerRef()
        && this.value() !== undefined
      ) {
        this.writeValue(this.value() || '');
      }
    });

  }

  writeValue(val: string) {
    // Ensure we always have a valid string value
    const safeValue = val != null ? String(val) : '';

    // Only update if the value actually changed
    if (this.#value !== safeValue) {
      this.#value = safeValue;

      // fix for value change while dispose in process
      setTimeout(() => {
        if (this.#editorInstance) {
          // guard the echo: Monaco fires onDidChangeModelContent synchronously for
          // this programmatic write; the flag tells that handler not to push it back
          // out through onChange.
          this.#isWritingValue = true;
          this.#editorInstance.setValue(safeValue);
          this.#isWritingValue = false;
        }
      });
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  ngOnDestroy() {
    this.#unsubscribeResize();
    if (!this.#editorInstance) { return; }
    this.#editorInstance.dispose();
  }

  // No base URL configured means no Zerops schemas — better than validating against
  // another environment's API.
  #buildSchemas() {
    if (!this.#schemaBaseUrl) {
      return [];
    }

    return [
      {
        uri: `${this.#schemaBaseUrl}/settings/import-project-yml-json-schema.json`,
        fileMatch: [
          '**/*zerops-import*',
          '**/zerops-project-import.yml',
          '**/zerops-service-import.yml'
        ]
      },
      {
        uri: `${this.#schemaBaseUrl}/settings/zerops-yml-json-schema.json`,
        fileMatch: [
          '**/*zerops-yml*',
          '**/zerops.yml'
        ]
      }
    ];
  }

  #initMonaco() {

    if (this.#editorInstance) {
      this.#editorInstance.dispose();
    }

    const editorModel = this.editorModel();
    const finalOptions = {
      ...this.#editorOptions()
    };

    if (!!editorModel) {
      const uri = editorModel.fileName
        ? (window as any).monaco.Uri.parse(editorModel.fileName)
        : undefined;
      const model = uri
        ? (window as any).monaco.editor.getModel(uri)
        : undefined;
      if (model) {
        finalOptions.model = model;
        finalOptions.model.setValue(this.#value || '');
      } else {
        finalOptions.model = (window as any).monaco.editor.createModel(
          editorModel.value || this.#value,
          editorModel.language,
          uri
        );
      }
    }

    this.#zone.runOutsideAngular(() => {
      this.#editorInstance = (window as any).monaco.editor.create(
        this.editorContainerRef().nativeElement,
        finalOptions
      );
    });

    if (!editorModel) {
      this.#editorInstance.setValue(this.#value || '');
    }

    this.#editorInstance.onDidChangeModelContent(() => {
      const value = this.#editorInstance.getValue();

      // a programmatic writeValue triggered this change - do NOT echo it back through
      // onChange (the ngrx-forms/store callback), or a stale/empty seed can race with
      // and overwrite an async form value set moments earlier. valueChange is left
      // intact so non-form consumers keep their existing behaviour.
      if (this.#isWritingValue) {
        this.#zone.run(() => {
          this.#value = value;
          this.valueChange.emit(value);
        });
        return;
      }

      this.#zone.run(() => {
        this.onChange(value);
        this.#value = value;
        this.valueChange.emit(value);
      });

    });

    this.#editorInstance.onDidBlurEditorWidget(() => {
      this.onTouched(undefined);
    });

    if (this.autoGrow()) {
      const applyAutoHeight = () => {
        const monaco = (window as any).monaco;
        const lineHeight = this.#editorInstance.getOption(monaco.editor.EditorOption.lineHeight) || 19;
        const padding = (CODE_FIELD_DEFAULT_OPTIONS.padding?.top || 0)
          + (CODE_FIELD_DEFAULT_OPTIONS.padding?.bottom || 0);
        const minHeight = (this.minLines() * lineHeight) + padding;
        const maxHeight = (this.maxLines() * lineHeight) + padding;
        // clamp content height into [minHeight, maxHeight]; ordering keeps the
        // min floor authoritative even if maxLines is misconfigured below minLines
        const height = Math.max(minHeight, Math.min(maxHeight, this.#editorInstance.getContentHeight()));
        const el = this.editorContainerRef().nativeElement;
        el.style.height = `${height}px`;
        this.#editorInstance.layout({ width: el.clientWidth, height });
      };
      this.#editorInstance.onDidContentSizeChange(() => applyAutoHeight());
      applyAutoHeight();
    }

    // refresh layout on resize event.
    this.#unsubscribeResize();
    this.#windowResizeSubscription = fromEvent(window, 'resize').subscribe(
      () => this.#editorInstance?.layout()
    );
    this.editorInit.emit(this.#editorInstance);
  }

  #unsubscribeResize() {
    if (this.#windowResizeSubscription) {
      this.#windowResizeSubscription.unsubscribe();
    }
  }

}
